import { useState } from "react";
import { CheckCircle2, PenLine } from "lucide-react";
import { formatUtcDate } from "@/utils/common.utils";
import { earnestMoneyInstruction, formatScheduledAt, nextClientJourneyItems, resolveTaskOwner, taskLabel, whoseTurnNow } from "@/utils/deals-view.utils";
import ConfirmCompleteDialog from "./ConfirmCompleteDialog";

type AnyRecord = Record<string, any>;

/**
 * The "what's next, and whose turn is it" hero, shared by every deal-room
 * portal. Originated as the client-only `ActionItems` in
 * `ClientDealView/ClientDealEngagement.tsx`; generalized here with a
 * `viewerRole` so a lender or attorney gets the same spotlighted-next-item
 * card in their own voice, off `resolveTaskOwner`/`whoseTurnNow`'s
 * viewer-relative wording rather than the client-only defaults.
 *
 * Marking complete always goes through `ConfirmCompleteDialog` — this is the
 * one checkbox on every portal, so it's also the one place external users can
 * fat-finger a completion by accident.
 */
interface PortalActionCardProps {
  /** The viewer's own open+complete tasks — `myNext` is the first incomplete one. */
  tasks: AnyRecord[];
  /** The broader visible checklist, for the "whose turn" fallback when the viewer has nothing open. */
  checklist?: AnyRecord[];
  team?: AnyRecord[];
  deal?: AnyRecord | null;
  /** "client" (default), or the external participant's own role — "lender", "attorney", "co_agent", "vendor". */
  viewerRole?: string;
  onComplete: (task: AnyRecord) => void | Promise<void>;
  testId?: string;
}

export default function PortalActionCard({
  tasks,
  checklist = [],
  team = [],
  deal = null,
  viewerRole = "client",
  onComplete,
  testId = "portal-action-items",
}: PortalActionCardProps) {
  const isClientViewer = viewerRole === "client";
  const [confirmTask, setConfirmTask] = useState<AnyRecord | null>(null);
  const [busy, setBusy] = useState(false);

  const myNext = tasks.filter((t) => t.status !== "complete")[0] || null;
  const checklistNext = nextClientJourneyItems(checklist, 1)[0] || null;
  const next = myNext || checklistNext;
  const owner = next ? resolveTaskOwner(next, team as any, deal, viewerRole) : null;
  // The hero already names its owner, so "who's on it now" lists everyone else —
  // otherwise both columns say the same task and person.
  const inFlight = myNext ? [] : whoseTurnNow(checklist, team as any, 3, deal, viewerRole)
    .filter((row) => row.owner.role !== owner?.role);
  const hasSide = Boolean(myNext) || inFlight.length > 0;
  const dueText = myNext?.scheduled_at
    ? formatScheduledAt(myNext.scheduled_at)
    : myNext?.due_date
      ? formatUtcDate(myNext.due_date)
      : "No deadline";

  const confirm = async () => {
    if (!confirmTask || busy) return;
    setBusy(true);
    try {
      await onComplete(confirmTask);
      setConfirmTask(null);
    } finally {
      setBusy(false);
    }
  };

  const nextBody = next
    ? (next.description || "").trim()
      || (owner?.isSelf && isClientViewer ? earnestMoneyInstruction(next, deal) : null)
      || (owner?.isSelf
        ? (isClientViewer
          ? "Please take care of this item so we can keep your closing on track."
          : "Please take care of this item to keep things moving.")
        : (isClientViewer
          ? `We're waiting on ${owner?.label} for this one. Nothing needed from you.`
          : `Waiting on ${owner?.label} for this one. Nothing needed from you.`))
    : (isClientViewer
      ? "Nothing urgent on your side. Your agent will flag anything that needs your attention."
      : "Nothing urgent on your side right now.");

  return (
    <div
      className={`mb-8 grid min-w-0 gap-5 rounded-[8px] border border-dash-border bg-[#f5f7f1] p-4 shadow-[0_12px_30px_rgba(32,35,31,0.08)] animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 ease-out sm:p-5 md:items-center md:p-6 ${hasSide ? "md:grid-cols-[1fr_280px]" : ""}`}
      data-testid={testId}
    >
      <div className="flex min-w-0 items-start gap-5">
        <span className="flex size-[70px] shrink-0 items-center justify-center rounded-full bg-dash-greenDeep text-white shadow-sm">
          {myNext ? <PenLine className="size-7" /> : <CheckCircle2 className="size-8" />}
        </span>
        <div className="min-w-0 pt-1">
          {owner && (
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-dash-muted" data-testid="portal-action-owner">
              {owner.isSelf ? "Your turn" : `With ${owner.label}`}
            </div>
          )}
          <h2 className="font-display text-[22px] font-semibold leading-tight tracking-normal text-dash-ink md:text-[26px]">
            {next ? taskLabel(next) : "Nothing needed right now"}
          </h2>
          <p className="mt-2 max-w-xl text-[15px] leading-6 text-dash-muted">{nextBody}</p>
        </div>
      </div>

      {hasSide && (
      <div className="border-t border-dash-border pt-4 md:border-l md:border-t-0 md:pl-7 md:pt-0">
        {myNext ? (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-dash-muted">Deadline</div>
            <div className="mt-1 font-display text-[26px] font-semibold leading-none tracking-normal text-dash-ink">
              {dueText}
            </div>
            <label
              className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-xl border border-dash-border bg-white px-3 py-2.5 text-[13px] leading-5 text-dash-ink transition-colors hover:bg-dash-hover"
              data-testid={`portal-action-${myNext.id}`}
            >
              <input
                type="checkbox"
                checked={false}
                disabled={busy}
                onChange={() => setConfirmTask(myNext)}
                className="mt-[3px] size-4 shrink-0 accent-dash-greenDeep"
              />
              <span className="min-w-0">{taskLabel(myNext)}</span>
            </label>
          </>
        ) : inFlight.length > 0 ? (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-dash-muted">Who's on it now</div>
            <ul className="mt-3 flex flex-col gap-3" data-testid="portal-whose-turn">
              {inFlight.map(({ owner: rowOwner, task }) => (
                <li key={task.id} className="min-w-0">
                  <div className="text-[13px] font-semibold capitalize leading-tight text-dash-ink">{rowOwner.label}</div>
                  <div className="mt-0.5 text-[12px] leading-[1.45] text-dash-muted">{taskLabel(task)}</div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
      )}

      <ConfirmCompleteDialog
        open={Boolean(confirmTask)}
        taskTitle={taskLabel(confirmTask)}
        onOpenChange={(open) => { if (!open) setConfirmTask(null); }}
        onConfirm={confirm}
      />
    </div>
  );
}
